import classNames from 'classnames';
import React, { useState } from 'react';

import { UiMultiply, UiCheckCircle } from '@vyaguta/vyaguta-icons/ui';

import { OVERLAY_TIMEOUT, SKILL_STATUS, VERIFIED } from 'constants/skills';

import { FlaggedRatingSelector } from 'components/common/skills';
import { SkillBreadCrumb } from 'components/common/skills';
import type { OverlaySkill } from 'components/common/skills/types';
import type { SkillStatusDetail } from 'interface/skillTree';
import { SkillWithStatus, SkillNode } from 'interface/skillTree';
import { Rating } from 'interface/skill';

interface SkillReviewOverlayProps {
  isOpenFor: OverlaySkill<SkillNode> | null;
  setIsOverlayOpenFor: React.Dispatch<React.SetStateAction<OverlaySkill<SkillNode> | null>>;
  globalRatingScale: Rating[];
  employee: { id: number; empId: string; firstName: string; teamManager?: { id: number } };
  userSkills?: SkillWithStatus[];
  setRefreshUserSkills: React.Dispatch<React.SetStateAction<boolean>>;
}

const SkillReviewOverlay: React.FC<SkillReviewOverlayProps> = (props) => {
  const { isOpenFor, setIsOverlayOpenFor, globalRatingScale, employee, setRefreshUserSkills } = props;
  const [closeOverlay, setCloseOverlay] = useState(false);

  if (!isOpenFor) {
    return null;
  }

  const skillStatusDetail =
    typeof isOpenFor.skillStatus === 'object' && isOpenFor.skillStatus !== null
      ? (isOpenFor.skillStatus as SkillStatusDetail)
      : null;
  const breadcrumbSkills = isOpenFor.parentData.reduce<Array<{ id: number | string; name: string }>>((items, skill) => {
    if (skill.name) {
      items.push({ id: skill.id, name: skill.name });
    }

    return items;
  }, []);
  const skillRatingScale =
    'localRating' in isOpenFor && Array.isArray(isOpenFor.localRating) ? isOpenFor.localRating : globalRatingScale;
  const selectedSkill: SkillWithStatus = {
    id: isOpenFor.id,
    name: isOpenFor.name,
    rating: isOpenFor.rating,
    skillStatus: skillStatusDetail ?? undefined,
  };
  const description = 'description' in isOpenFor ? isOpenFor.description : undefined;

  const handleOverlayClose = (shouldRefetch: boolean) => {
    setCloseOverlay(true);

    setTimeout(() => {
      setIsOverlayOpenFor(null);

      if (shouldRefetch) {
        setRefreshUserSkills((prev) => !prev);
      }

      setCloseOverlay(false);
    }, OVERLAY_TIMEOUT);
  };

  return (
    <div
      className={classNames('skill-overlay', {
        'skill-overlay--close': closeOverlay,
        'skill-overlay--open': !!isOpenFor,
      })}
    >
      <div className="skill-overlay__header">
        <div className="title">Skills Details</div>
        <div className="close" onClick={() => handleOverlayClose(false)}>
          <UiMultiply />
        </div>
      </div>
      <div className="mb-20">
        <SkillBreadCrumb skills={breadcrumbSkills} />
      </div>
      <div className="skill-heading">
        <div className="skill-heading__container">
          <div className="title">{isOpenFor.name}</div>
          {skillStatusDetail?.status === SKILL_STATUS.APPROVED && (
            <div className="verified-skill">
              <UiCheckCircle size={14} />
              {VERIFIED}
            </div>
          )}
        </div>
        <div className="description">{description}</div>
      </div>
      <div className="mt-15">
        <FlaggedRatingSelector
          skillRatingScale={skillRatingScale}
          skill={selectedSkill}
          userRating={isOpenFor.rating}
          employee={employee}
          handleOverlayClose={handleOverlayClose}
        />
      </div>
    </div>
  );
};

export default SkillReviewOverlay;
